import { useEffect } from 'react'
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom'
import { useAuthStore } from '@/store/auth'
import { connectStoreToGateway, useAppStore } from '@/store/app'
import { usePreferencesStore } from '@/store/preferences'
import { anunciarVozAoReconectar, useVoiceStore } from '@/store/voice'
import { onGlobalShortcut, updateBadge } from '@/lib/desktop'
import { useTotalUnread } from '@/hooks/selectors'
import { Spinner } from '@/components/ui'
import { AuthView } from '@/views/AuthView'
import { AppView } from '@/views/AppView'
import { AdminView } from '@/views/AdminView'
import { InviteView } from '@/views/InviteView'
import { LandingView } from '@/views/LandingView'
import { VoiceAudioOutput } from '@/components/voice/VoiceAudioOutput'

export function App() {
  const restore = useAuthStore((s) => s.restore)
  const status = useAuthStore((s) => s.status)
  const reset = useAppStore((s) => s.reset)
  const fontScale = usePreferencesStore((s) => s.ui.fontScale)
  const reduceMotion = usePreferencesStore((s) => s.ui.reduceMotion)

  useEffect(() => {
    void restore()
    const desligarStore = connectStoreToGateway()
    const desligarVoz = anunciarVozAoReconectar()
    return () => {
      desligarStore()
      desligarVoz()
    }
  }, [restore])

  // Sair precisa limpar o estado do aplicativo; senão o próximo login veria por
  // um instante os servidores da conta anterior.
  useEffect(() => {
    if (status === 'anonymous') reset()
  }, [status, reset])

  useEffect(() => {
    document.documentElement.style.fontSize = `${fontScale * 100}%`
  }, [fontScale])

  useEffect(() => {
    document.documentElement.classList.toggle('motion-reduce', reduceMotion)
  }, [reduceMotion])

  // Atalhos globais do desktop: silenciar sem trazer a janela para a frente é
  // o que torna a chamada usável enquanto se trabalha em outro aplicativo.
  useEffect(
    () =>
      onGlobalShortcut((action) => {
        const voice = useVoiceStore.getState()
        if (!voice.room) return
        if (action === 'toggle-mute') void voice.toggleMute()
        else voice.toggleDeafen()
      }),
    [],
  )

  // Contador de não lidas no ícone da barra de tarefas, ou no título da aba.
  const unreadTotal = useTotalUnread()

  useEffect(() => {
    updateBadge(unreadTotal)
  }, [unreadTotal])

  return (
    <BrowserRouter>
      {/*
        Fica fora das rotas de propósito.

        Enquanto morava dentro da tela do aplicativo, abrir o painel de
        administração desmontava os elementos de áudio e a pessoa parava de ouvir
        os outros, embora continuasse sendo ouvida: o microfone segue publicando
        porque a sessão de voz vive na store, não na tela. Aqui em cima o áudio
        acompanha a chamada em qualquer rota.
      */}
      <VoiceAudioOutput />

      <Routes>
        {/* A landing é a porta de entrada de quem chega sem sessão. Quem já
            está autenticado vai direto para o aplicativo. */}
        <Route path="/" element={<RootRoute />} />
        <Route path="/baixar" element={<LandingView />} />
        <Route path="/entrar-conta" element={<AuthView mode="login" />} />
        <Route path="/cadastro" element={<AuthView mode="register" />} />
        <Route path="/entrar" element={<InviteView />} />
        <Route path="/entrar/:code" element={<InviteView />} />

        <Route
          path="/app"
          element={
            <RequireAuth>
              <AppView />
            </RequireAuth>
          }
        />
        <Route
          path="/admin"
          element={
            <RequireAuth requireAdmin>
              <AdminView />
            </RequireAuth>
          }
        />

        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </BrowserRouter>
  )
}

/**
 * Raiz do site.
 *
 * Sessão ativa cai no aplicativo; visitante vê a página de apresentação. Sem
 * isto, quem chega pelo endereço divulgado encontraria uma tela de login sem
 * saber do que se trata.
 */
function RootRoute() {
  const status = useAuthStore((s) => s.status)

  if (status === 'loading') {
    return (
      <div className="flex h-dvh items-center justify-center bg-surface-base">
        <Spinner className="size-8 text-brand" />
      </div>
    )
  }

  return status === 'authenticated' ? <AppView /> : <LandingView />
}

function RequireAuth({
  children,
  requireAdmin,
}: {
  children: React.ReactNode
  requireAdmin?: boolean
}) {
  const status = useAuthStore((s) => s.status)
  const isAdmin = useAuthStore((s) => s.user?.isAdmin ?? false)
  const location = useLocation()

  if (status === 'loading') {
    return (
      <div className="flex h-dvh items-center justify-center bg-surface-base">
        <Spinner className="size-8 text-brand" />
      </div>
    )
  }

  if (status === 'anonymous') {
    // Guarda o destino para voltar aqui depois do login.
    return <Navigate to="/entrar-conta" state={{ from: location.pathname }} replace />
  }

  if (requireAdmin && !isAdmin) {
    return <Navigate to="/" replace />
  }

  return <>{children}</>
}
